import { useState, type ReactNode } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { ChangeTodaySheet } from "../components/ChangeTodaySheet";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Notice } from "../components/Notice";
import { Ring } from "../components/Ring";
import { NavRow, RowGroup, type RowTone } from "../components/Rows";
import { Screen } from "../components/Shell";
import { TopBar } from "../components/TopBar";
import { testsRowValue } from "../domain/fitnessTests";
import { allMoves } from "../domain/moves";
import { cycleOn, cycleRegularity } from "../domain/cycle";
import { careWalkDay } from "../domain/cycleCare";
import { DOW_LABELS, dayLabel, fmtClock, parseDay, todayStr } from "../domain/date";
import { sessionKcal } from "../domain/energy";
import { phase, sessionFor, sessionKeyFor, typeName, weekNo } from "../domain/plan";
import { programmeFor } from "../domain/programme";
import { sessionMinutes, sessionPct } from "../domain/queue";
import { SESSION_LABEL } from "../domain/session";
import { hasTreadmill, tmProtocol, tmProtocolLine, tmTotalSecs } from "../domain/treadmill";
import { answeredCount, askVigorous, easyTreadmillLine, retestDue, sessionGate, trainingSteps } from "../domain/trainingQuestions";
import { backAfterBreak, backLine, morningLine, phasePinLine } from "../domain/readiness";
import { completedWeek } from "../domain/weekDone";
import { WeekTicks } from "../components/WeekTicks";
import { setTrainingProfile } from "../store/toggles";
import { walkSecs } from "../domain/walk";
import { dayMinutes, sessionProgress, weekDone, weekRows, type WeekRow } from "../domain/week";
import { beep } from "../lib/device";
import { useDeviceAnswers, useKaya, usePlayerMode } from "../store/kayaStore";
import { usePlayerIntent } from "../store/playerIntent";
import TrainSteady from "./TrainSteady";
import { YogaExtraRow } from "./yogaExtra";
import "./Train.css";

/** The right-hand word for a day — every state in words, never a letter or a colour alone. */
function dayValue(r: WeekRow): string | undefined {
  switch (r.state) {
    case "done":
      return "done";
    case "rescued":
      return "rescued";
    case "frozen":
      return "freeze used";
    case "rested":
      return "rest · streak safe";
    case "missed":
      return "missed";
    case "before":
      return "before the plan";
    case "started":
      return r.progress ? `${r.progress.done} of ${r.progress.total}` : undefined;
    case "planned":
      return r.minutes ? `${r.minutes} min` : undefined;
    default:
      return undefined;
  }
}

function daySub(r: WeekRow): string | undefined {
  const parts: string[] = [];
  if (r.isToday) parts.push("Today");
  if (r.swapped) parts.push("swapped in");
  else if (r.travel) parts.push("travel day");
  if (r.state === "rescued") parts.push("15-minute rescue");
  if (r.period) parts.push("period");
  if (r.treadmill) parts.push("treadmill done");
  if (!parts.length) return undefined;
  const s = parts.join(" · ");
  return s[0].toUpperCase() + s.slice(1);
}

function dayIcon(r: WeekRow): { icon: ReactNode; tone: RowTone } {
  if (r.state === "done") return { icon: I.check, tone: "success" };
  if (r.state === "rescued") return { icon: I.bandage, tone: "caution" };
  if (r.state === "frozen") return { icon: I.snowflake, tone: "sky" };
  return {
    icon: (
      <span className="train-ring">
        <Ring pct={r.pct} size={22} stroke={3} color="var(--brand)" />
      </span>
    ),
    tone: "default",
  };
}

/** The Train tab: the Kaya programme's, or the Steady programme's for a person on it (ADR-052). */
export default function Train() {
  const steady = useKaya((s) => !!s.S.profile.training && programmeFor(s.S) === "steady");
  return steady ? <TrainSteady /> : <TrainKaya />;
}

/**
 * The Train tab: today's session and how to start it, the week in words, then the cardio and the
 * reference pages. Replaces Plan and the week strip; the treadmill rows go with the machine
 * (ADR-022), the walks stay.
 */
function TrainKaya() {
  const S = useKaya((s) => s.S);
  // One session at a time: with one open, every Start goes back to it. (ADR-027)
  const session = useKaya((s) => s.session);
  const history = useHistory();
  const intent = usePlayerIntent();
  const device = useDeviceAnswers();
  const pm = usePlayerMode();
  const [changing, setChanging] = useState(false);

  const today = todayStr();
  // the safety answers on today's session: held, no session, or no fast work (P9, sessionGate)
  const gate = sessionGate(S, sessionKeyFor(S, today), device);
  const key = gate.key;
  const s = sessionFor(key);
  const p = phase(S);
  const pct = sessionPct(S, today, key) ?? 0;
  const prog = sessionProgress(S, today, key);
  const log = S.logs[today];
  const rows = weekRows(S, today, pm.simple);
  const wk = weekDone(rows);
  // the completed week as "of" counts and ticks (ADR-066); the morning after and the first one
  // back as words (ADR-065)
  const week = completedWeek(S, today, pm.simple);
  const sore = morningLine(S, today);
  const resumeLabel = session ? `Resume ${SESSION_LABEL[session.kind].toLowerCase()}` : null;

  /** Minimise and End come back here rather than to the player's own default. */
  const player = (route: string) => history.push(route, { from: history.location.pathname });
  const startOrResume = (go: () => void) => {
    if (session) player(session.route);
    else go();
  };
  const startSession = () => player(session ? session.route : "/workout");
  const startWalk = () =>
    startOrResume(() => {
      // beeping here, inside the click, is also what unlocks the AudioContext for the player
      beep(880, 120, 0.15, S.profile.sound);
      player("/walk");
    });

  const treadmill = hasTreadmill(S);
  const tmDone = log?.treadmill ?? null;
  const tmMin = Math.round(tmTotalSecs(tmProtocol(S, gate.easyTreadmill)) / 60);
  const walks = log?.walks ?? [];
  const reg = cycleOn(S) ? cycleRegularity(S) : null;
  // Cycle care is offered for an irregular cycle, and in the week before a period with its walk
  // added (ADR-057)
  const careFlag = (!!reg && (reg.irregular || reg.late || reg.absent)) || careWalkDay(S, today);
  const careMin = careFlag ? sessionMinutes(S, "Y", p, { ds: today }) : 0;
  const careDone = (log?.extras ?? []).includes("Y");
  const programs = S.profile.programs ?? [];
  const moves = allMoves().filter((e) => !(e.hipfree && S.profile.hipSafe)).length;

  const rest = !s.blocks.length;

  // "Plan my exercise" (ADR-053): the row's value is how far the answers got, or why the plan is held
  const meta = useKaya((st) => st.meta);
  const update = useKaya((st) => st.update);
  const held = meta.trainingSafety?.gate === "doctor";
  const answered = answeredCount(S, new Date(), { gate: held, safetyAnswered: !!meta.trainingSafety });
  const due = retestDue(S);
  const testsValue = testsRowValue(S, today);
  const planValue = answered
    ? `${answered.n === answered.of ? `${answered.n} answers` : `${answered.n} of ${answered.of} answered`}${due ? ` · retest ${dayLabel(due)}` : ""}`
    : meta.trainingDraft
      ? "Carry on"
      : `${trainingSteps(S, {}).length} questions`;
  const vigorous = !rest && askVigorous(S, gate.from ?? key);
  // "too hard" twice pinned the phase (ADR-054); the tests row is how the retest is reached
  const pinned = phasePinLine(S);
  const back = backAfterBreak(S, today);
  const offerBack = back !== null && !rest && !gate.stop && pct === 0 && !log?.rescueDone && meta.breakNoticeOff !== today;
  const setBreakNoticeOff = useKaya((st) => st.setBreakNoticeOff);

  const primary = gate.stop
    ? { label: "Start session", onClick: () => undefined, icon: I.play, disabled: true, reason: "A lower BP reading, under Plan my exercise, brings it back" }
    : rest
    ? { label: resumeLabel ?? "Start a walk", onClick: startWalk, icon: I.walk }
    : {
        // A walk or a treadmill run already open: the Start goes to it, and says so.
        label:
          session && session.kind !== "workout"
            ? (resumeLabel ?? "Resume")
            : pct >= 1
              ? "Done — view"
              : pct > 0 && prog
                ? `Continue · ${prog.done} of ${prog.total}`
                : "Start session",
        onClick: startSession,
        icon: pct >= 1 ? I.check : I.play,
      };

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="train-page">
            <TopBar title="Train" sub={`Week ${weekNo(S)} · phase ${p + 1}`} />

            {gate.stop ? (
              <Hero label={`Today · ${dayLabel(today)}`} title="No session today" line={gate.line ?? undefined} />
            ) : rest ? (
              <Hero
                label={`Today · ${dayLabel(today)}`}
                title="Rest day · optional 20-minute walk"
                line={`${week.line} so far`}
                sub={s.how}
                note="A rest day — an easy walk, or the yoga and breathing session below."
              />
            ) : (
              <Hero
                label={`Today · ${dayLabel(today)}`}
                title={typeName(key)}
                line={`${dayMinutes(S, today, key, pm.simple)} min${prog ? ` · ${prog.total} moves` : ""}`}
                sub={sore ?? `~${sessionKcal(S, key)} kcal est.`}
                note={gate.line ?? undefined}
                visual={
                  <Ring
                    pct={pct}
                    size={72}
                    stroke={7}
                    color="var(--brand)"
                    figure={prog ? `${prog.done}/${prog.total}` : null}
                  />
                }
                visualSide
                onPress={() => history.push("/train/session")}
                ariaLabel={`Today's session: ${typeName(key)}, ${prog ? `${prog.done} of ${prog.total} moves done` : ""}. Show the moves`}
              />
            )}

            <WeekTicks week={week} />

            {offerBack ? (
              <Notice
                tone="info"
                title="Back after a break"
                primary={{
                  label: "Rescue session",
                  onClick: () =>
                    startOrResume(() => {
                      // the 15-minute rescue counts today, as Today's rescue counts a missed day
                      update((d) => void (d.rescueFor = today));
                      intent.setRescue();
                      player("/workout");
                    }),
                }}
                secondary={{ label: "My usual", onClick: () => setBreakNoticeOff(today) }}
              >
                {backLine(back, false)}
              </Notice>
            ) : null}

            <RowGroup heading={`This week · ${wk.done} of ${wk.of} done`}>
              {rows.map((r) => {
                const { icon, tone } = dayIcon(r);
                return (
                  <NavRow
                    key={r.ds}
                    title={`${DOW_LABELS[parseDay(r.ds).getDay()]} · ${r.name}`}
                    subtitle={daySub(r)}
                    icon={icon}
                    tone={tone}
                    value={dayValue(r)}
                    to={r.isToday && !rest ? "/train/session" : undefined}
                  />
                );
              })}
            </RowGroup>

            {vigorous ? (
              <Notice
                tone="info"
                title="Used to hard effort?"
                primary={{ label: "Yes", onClick: () => update((d) => void setTrainingProfile(d, { vigorousOk: true })) }}
                secondary={{ label: "Not yet", onClick: () => update((d) => void setTrainingProfile(d, { vigorousOk: false })) }}
              >
                Today has fast work. From 40, Kaya asks once; on Not yet it builds up to it first.
              </Notice>
            ) : null}

            {pinned ? (
              <Notice
                tone="info"
                primary={{ label: "Change", onClick: () => history.push("/you/session") }}
                onDismiss={() => update((d) => void setTrainingProfile(d, { phasePin: undefined }))}
              >
                {pinned}
              </Notice>
            ) : null}

            <RowGroup heading="Your plan">
              <NavRow
                icon={I.list}
                title="Plan my exercise"
                subtitle={held ? "Held at easy walks: talk to your doctor first" : undefined}
                value={planValue}
                to="/train/questions"
              />
              {/* once there are tests their figures are here, not only when the retest is due (P9) */}
              {!held && (answered || testsValue) ? (
                <NavRow icon={I.balance} title="Your tests" value={testsValue ?? "not done yet"} to="/train/tests" />
              ) : null}
            </RowGroup>

            <RowGroup heading="Cardio">
              {treadmill ? (
                <NavRow
                  title="Treadmill intervals"
                  subtitle={easyTreadmillLine(S, device) ?? tmProtocolLine(S)}
                  icon={I.treadmill}
                  tone="caution"
                  value={tmDone ? `Done · ${tmDone.km ? tmDone.km.toFixed(1) + " km" : "–"}` : `${tmMin} min`}
                  to="/train/treadmill"
                />
              ) : null}
              <NavRow
                title="Walks"
                icon={I.walk}
                tone="sky"
                value={walks.length ? `${walks.length} today · ${fmtClock(walkSecs(walks) * 1000)}` : "None yet today"}
                to="/train/walks"
              />
              {careFlag ? (
                careDone ? (
                  <NavRow kind="value" title={`Cycle care · ${careMin} min`} icon={I.drop} tone="cycle" value="done today" />
                ) : (
                  <NavRow
                    title={`Cycle care · ${careMin} min`}
                    subtitle={
                      careWalkDay(S, today)
                        ? "Yoga and breathing, then a walk — a walk eases premenstrual symptoms"
                        : "Yoga and breathing — why it helps is on Progress › Cycle"
                    }
                    icon={I.drop}
                    tone="cycle"
                    onClick={() =>
                      startOrResume(() => {
                        intent.setExtra("Y");
                        player("/workout");
                      })
                    }
                  />
                )
              ) : null}
              <YogaExtraRow />
            </RowGroup>

            <RowGroup heading="Reference">
              <NavRow title="Exercise library" icon={I.book} value={`${moves} moves`} to="/train/library" />
              {treadmill ? (
                <NavRow
                  title="Treadmill programs"
                  icon={I.list}
                  value={programs.length ? `${programs.length} saved` : "None yet"}
                  to="/train/programs"
                />
              ) : null}
            </RowGroup>

            <ActionBar primary={primary} secondary={{ label: "Change", onClick: () => setChanging(true) }} />
          </div>
        </Screen>
        <ChangeTodaySheet open={changing} onClose={() => setChanging(false)} ds={today} />
      </IonContent>
    </IonPage>
  );
}
